import {
  Button,
  HStack,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useTranslations } from 'use-intl';

import {
  AssetType,
  BalanceInfosFragment,
  ContractType,
  WalletInfosFragment,
} from '@blockpulse3/graphql/hooks';
import { formatNumberCurrency } from '@blockpulse3/helpers';
import { AssetBadge, CompanyIdentityAvatar, TableContainer } from '@blockpulse3/ui/commons';

import { OptionAssetSidePanel } from './OptionAssetSidePanel';
import { StockAssetSidePanel } from './StockAssetSidePanel';
import { WalletEmptyTable } from './WalletEmptyTable';
import { MergedStockAssetByType } from './types';
import { useIdentityId } from './useIdentityId';
import { getMergedBalances } from './utils';

type Props = {
  balances: WalletInfosFragment['balances'];
};

/**
 * Wallet Table.
 *
 * @returns {JSX.Element}
 */
export function WalletTable({ balances }: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nWallet = useTranslations('Wallet');

  /* ** Side panel infos ** */
  const [sidePanelRowOptionAsset, setSidePanelRowOptionAsset] =
    useState<BalanceInfosFragment | null>(null);
  const [sidePanelRowStockAsset, setSidePanelRowStockAsset] =
    useState<MergedStockAssetByType | null>(null);

  /* ** Side panel disclosures ** */
  const stockAssetSidePanel = useDisclosure();
  const optionAssetSidePanel = useDisclosure();

  const identityId = useIdentityId();

  /* ** Merge balances row by companyName and assest type ** */
  const mergedStockAssetByType = getMergedBalances(balances);

  const handleRowClick = (row: MergedStockAssetByType): void => {
    if (!identityId) {
      return;
    }
    setSidePanelRowStockAsset(row);
    setSidePanelRowOptionAsset(row.balances[0]);

    switch (row.balances[0].asset.contractType) {
      /* ** Option Token ** */
      case ContractType.OPTION_TOKEN: {
        optionAssetSidePanel.onOpen();
        break;
      }
      /* ** Stock Token (or old version Basic Token) ** */
      default: {
        stockAssetSidePanel.onOpen();
        break;
      }
    }
  };

  if (!balances?.length) {
    return <WalletEmptyTable />;
  }

  return (
    <Stack>
      <StockAssetSidePanel
        identityId={identityId}
        isOpen={stockAssetSidePanel.isOpen}
        mergedBalances={sidePanelRowStockAsset}
        onClose={stockAssetSidePanel.onClose}
      />
      <OptionAssetSidePanel
        balance={sidePanelRowOptionAsset}
        identityId={identityId}
        isOpen={optionAssetSidePanel.isOpen}
        onClose={optionAssetSidePanel.onClose}
      />
      <TableContainer>
        <Table variant="striped">
          <Thead>
            <Tr>
              <Th>{i18nWallet('CompanyName')}</Th>
              <Th>{i18nWallet('OwnedShares')}</Th>
              <Th isNumeric>{i18nWallet('InvestAmount')}</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {mergedStockAssetByType?.map((balance, index) => {
              return (
                <Tr key={index} role="button" onClick={(): void => handleRowClick(balance)}>
                  <Td fontWeight="600">
                    <HStack>
                      <CompanyIdentityAvatar src={balance.company.profilePicture} />
                      <Text>{balance.company.name}</Text>
                    </HStack>
                  </Td>
                  <Td>
                    <Stack spacing="2">
                      {balance.subscribedAmountOrdinaryShare > 0 && (
                        <AssetBadge
                          type={AssetType.ORDINARY_SHARE}
                          value={balance.subscribedAmountOrdinaryShare}
                        />
                      )}
                      {balance.subscribedAmountPreferedShare > 0 && (
                        <AssetBadge
                          type={AssetType.PREFERRED_SHARE}
                          value={balance.subscribedAmountPreferedShare}
                        />
                      )}
                      {balance.subscribedAmount > 0 && (
                        <AssetBadge
                          type={balance.balances[0].asset.type}
                          value={balance.subscribedAmount}
                        />
                      )}
                    </Stack>
                  </Td>
                  <Td isNumeric fontWeight="600">
                    <Text>{formatNumberCurrency(balance.investAmount)}</Text>
                  </Td>
                  <Td align="right" textAlign="right">
                    <Button variant="secondary">{i18nGeneral('DetailsButton')}</Button>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

export type WalletTableProps = Props;
